import { Image, Pencil, RotateCcw, Trash2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from 'uuid';

type VariantOption = {
  id?: string; // Optional ID for existing variants
  attributes: Record<string, string>;
  mrp: number;
  discountPercent: number;
  stock: number;
  sku: string;
  existingImages: string[]; // Existing URLs from backend
  images: File[];           // New uploads
  discountedPrice: number;
};

type ProductVariantConfig = {
  hasVariants: boolean;
  attributes: string[];
  variantOptions: Record<string, string[]>;
  variants: VariantOption[];
};

interface ProductVariantManagerProps {
  variantConfig: ProductVariantConfig;
  onVariantConfigChange: (config: ProductVariantConfig) => void;
  toast: {
    showToast: (message: string, type: "success" | "error" | "info") => void;
  };
  // Product name used to auto-suggest variant SKUs.
  productTitle?: string;
  // Parent-level values that can be copied down onto every variant.
  baseValues?: {
    mrp?: number;
    sellingPrice?: number;
    stock?: number;
  };
}

type VariantOptionDef = {
  id: string;
  name: string;
  values: string[];
  valueInput: string;
};

const defaultOptionNames = ["Color", "Size"];

// SKU-safe token from a label, capped by word and char count (mirrors the
// server's tokenize in ecommerce-server/src/utils/sku.ts).
function skuToken(label: string | undefined, maxWords: number, maxChars: number): string {
  return (label || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .split("-")
    .filter(Boolean)
    .slice(0, maxWords)
    .join("-")
    .slice(0, maxChars)
    .replace(/-+$/, "");
}

// Preview of the SKU the server will generate for a blank variant SKU, e.g.
// "CLASSIC-T-SHIRT-RED-M". Display only: the server appends a short random
// suffix to keep it unique, so this is never sent as the variant's SKU.
function autoSkuForCombo(title: string | undefined, combo: Record<string, string>): string {
  const parts = Object.values(combo).map(value => skuToken(value, 1, 6));
  return [skuToken(title, 3, 16) || "SKU", ...parts].filter(Boolean).join("-");
}

// A full SKU in the server's generated format (preview + random suffix), used to
// pre-fill the SKU field when a variant is customized. Once filled it's sent as an
// admin-entered SKU, so the suffix is what keeps it unique across products.
function generateSkuForCombo(title: string | undefined, combo: Record<string, string>): string {
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${autoSkuForCombo(title, combo)}-${suffix}`;
}

function getCombinations(defs: VariantOptionDef[]): Record<string, string>[] {
  if (defs.length === 0 || defs.some(d => d.values.length === 0 || !d.name)) return [];
  const combine = (idx: number, acc: Record<string, string>): Record<string, string>[] => {
    if (idx === defs.length) return [acc];
    const def = defs[idx];
    return def.values.flatMap(val => combine(idx + 1, { ...acc, [def.name]: val }));
  };
  return combine(0, {});
}

const ProductVariantManager: React.FC<ProductVariantManagerProps> = ({
  variantConfig,
  onVariantConfigChange,
  toast,
  productTitle,
  baseValues
}) => {
  const [optionDefs, setOptionDefs] = useState<VariantOptionDef[]>(() => {
    if (variantConfig.attributes.length > 0) {
      return variantConfig.attributes.map((name) => ({
        id: uuidv4(),
        name,
        values: variantConfig.variantOptions[name] || [],
        valueInput: ""
      }));
    }
    return [];
  });

  // State for variant details. We collect Selling Price (not discount %) from the
  // admin and derive discountPercent when syncing to the parent config.
  const [variantDetails, setVariantDetails] = useState<Record<string, {
    id?: string; // Variant ID for existing variants
    mrp: number | undefined;
    sellingPrice: number | undefined;
    stock: number | undefined;
    sku?: string; // Editable, auto-suggested when left blank
    existingImages: string[]; // Existing URLs from backend
    images: File[];           // New uploads
  }>>(() => {
    // Initialize from existing variantConfig if available
    if (variantConfig.hasVariants && variantConfig.variants.length > 0) {
      const initialDetails: Record<string, any> = {};
      variantConfig.variants.forEach(variant => {
        const key = Object.values(variant.attributes).join("||");
        // Prefer the stored discounted (selling) price; fall back to deriving it
        // from mrp + discountPercent for older records.
        const sellingPrice = variant.discountedPrice
          ? variant.discountedPrice
          : variant.mrp - (variant.mrp * (variant.discountPercent || 0)) / 100;
        initialDetails[key] = {
          id: variant.id, // Preserve existing variant ID
          mrp: variant.mrp,
          sellingPrice,
          stock: variant.stock,
          sku: variant.sku,
          existingImages: variant.existingImages || [],
          images: variant.images || [],
        };
      });
      console.log("Initialized variantDetails from existing config:", initialDetails);
      return initialDetails;
    }
    return {};
  });
  const [compressingVariants, setCompressingVariants] = useState<Record<string, boolean>>({});

  // Which variants the admin has chosen to customize (expanded editor). Any variant
  // not in this set simply inherits the product's price, stock, SKU and photos.
  // In edit mode we start with every saved variant expanded so its data is visible.
  const [customizedKeys, setCustomizedKeys] = useState<Set<string>>(() => {
    if (variantConfig.hasVariants && variantConfig.variants.length > 0) {
      return new Set(variantConfig.variants.map(v => Object.values(v.attributes).join("||")));
    }
    return new Set();
  });

  // Sync optionDefs to parent config
  const syncToConfig = (defs: VariantOptionDef[]) => {
    const attributes = defs.map((d) => d.name);
    const variantOptions: Record<string, string[]> = {};
    defs.forEach((d) => {
      variantOptions[d.name] = d.values;
    });
    
    // Generate combinations and sync variant details
    const combos = getCombinations(defs);
    console.log("combos:", combos);
    const variants: VariantOption[] = combos.map((combo, index) => {
      const key = defs.map(opt => combo[opt.name]).join("||");
      const details = variantDetails[key] || { mrp: undefined, sellingPrice: undefined, stock: undefined, sku: undefined, existingImages: [], images: [] };

      console.log(`Variant ${index} - key: ${key}, details:`, details);

      // Only send a SKU the admin typed. Blank goes to the server, which
      // generates a short, unique one (the preview is never sent).
      const sku = details.sku?.trim() || "";

      // Blank fields inherit the product-level defaults (baseValues).
      const mrp = details.mrp !== undefined ? details.mrp : (baseValues?.mrp ?? 0);
      const discountedPrice = details.sellingPrice !== undefined
        ? details.sellingPrice
        : (baseValues?.sellingPrice ?? mrp);
      const stock = details.stock !== undefined ? details.stock : (baseValues?.stock ?? 0);
      // Derive discount from MRP + Selling Price (the fields the admin edits).
      const discountPercent = mrp > 0 ? Math.max(0, ((mrp - discountedPrice) / mrp) * 100) : 0;

      // Preserve existing ID or generate new one
      const variantId = details.id || `variant_${Date.now()}_${index}`;

      return {
        id: variantId, // Include variant ID
        attributes: combo,
        mrp: mrp,
        discountPercent: discountPercent,
        stock: stock,
        sku,
        existingImages: details.existingImages,
        images: details.images,
        discountedPrice: discountedPrice
      };
    });
    
    console.log("Generated variants:", variants);
    
    // Set hasVariants based on whether we have valid variants
    const hasVariants = attributes.length > 0 && attributes.every(attr => variantOptions[attr] && variantOptions[attr].length > 0) && variants.length > 0;
    
    onVariantConfigChange({
      ...variantConfig,
      hasVariants,
      attributes,
      variantOptions,
      variants
    });
  };

  // Handle variant detail change
  const handleVariantDetailChange = (key: string, field: string, value: any) => {
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // Open the per-variant editor, pre-filling it with the inherited defaults and an
  // auto-generated SKU so the admin can tweak from there.
  const handleStartCustomize = (key: string, combo: Record<string, string>) => {
    setCustomizedKeys(prev => new Set(prev).add(key));
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        id: prev[key]?.id,
        mrp: prev[key]?.mrp ?? baseValues?.mrp,
        sellingPrice: prev[key]?.sellingPrice ?? baseValues?.sellingPrice,
        stock: prev[key]?.stock ?? baseValues?.stock,
        sku: prev[key]?.sku?.trim() || generateSkuForCombo(productTitle, combo),
        existingImages: prev[key]?.existingImages || [],
        images: prev[key]?.images || [],
      }
    }));
  };

  // Drop a variant's overrides so it goes back to inheriting from the product.
  const handleResetCustomize = (key: string) => {
    setCustomizedKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setVariantDetails(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Sync data when optionDefs change
  useEffect(() => {
    if (optionDefs.length > 0 && optionDefs.some(d => d.name && d.values.length > 0)) {
      syncToConfig(optionDefs);
    }
  }, [optionDefs]);

  // Sync data when variantDetails or the product title (used for SKU suggestions) change
  useEffect(() => {
    if (optionDefs.length > 0 && optionDefs.some(d => d.name && d.values.length > 0)) {
      console.log("=== PRODUCTVARIANTMANAGER DEBUG ===");
      console.log("optionDefs:", optionDefs);
      console.log("variantDetails:", variantDetails);
      syncToConfig(optionDefs);
    }
    // baseValues primitives are included so inherited (blank) rows re-sync when
    // the product-level defaults change.
  }, [variantDetails, optionDefs, productTitle, baseValues?.mrp, baseValues?.sellingPrice, baseValues?.stock]);

  // Add new option
  const handleAddOption = () => {
    setOptionDefs((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name: "",
        values: [],
        valueInput: ""
      }
    ]);
  };

  // Remove option
  const handleRemoveOption = (id: string) => {
    const newDefs = optionDefs.filter((o) => o.id !== id);
    setOptionDefs(newDefs);
    syncToConfig(newDefs);
  };

  // Edit option name
  const handleOptionNameChange = (id: string, name: string) => {
    setOptionDefs((prev) => {
      const updated = prev.map((o) => (o.id === id ? { ...o, name, values: [] } : o));
      return updated;
    });
  };

  // Add value to option
  const handleAddValue = (id: string) => {
    setOptionDefs((prev) => {
      const updated = prev.map((o) => {
        if (o.id !== id) return o;
        const val = o.valueInput.trim();
        if (!val || o.values.includes(val)) return o;
        return { ...o, values: [...o.values, val], valueInput: "" };
      });
      return updated;
    });
  };

  // Remove value from option
  const handleRemoveValue = (id: string, value: string) => {
    setOptionDefs((prev) => {
      const updated = prev.map((o) =>
        o.id === id ? { ...o, values: o.values.filter((v) => v !== value) } : o
      );
      return updated;
    });
  };

  // Handle value input change
  const handleValueInputChange = (id: string, value: string) => {
    setOptionDefs((prev) =>
      prev.map((o) => (o.id === id ? { ...o, valueInput: value } : o))
    );
  };

  // Handle image change for a variant
  const handleVariantImageChange = async (key: string, files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
    const invalid = arr.filter((f) => !allowedTypes.includes(f.type));
    if (invalid.length > 0) {
      toast.showToast("Some files are not valid images. Please use JPG, PNG, or WebP files.", "error");
      return;
    }
    setCompressingVariants(prev => ({ ...prev, [key]: true }));
    let compressed: File[] = [];
    try {
      const { compressImageFiles } = await import("../../utils/imageCompression");
      compressed = await compressImageFiles(arr);
    } finally {
      setCompressingVariants(prev => ({ ...prev, [key]: false }));
    }
    
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        images: [...(prev[key]?.images || []), ...compressed]
      }
    }));
  };

  // Remove image for a variant
  const handleRemoveVariantImage = (key: string, idx: number) => {
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        images: prev[key]?.images.filter((_, i) => i !== idx) || []
      }
    }));
  };

  // Remove existing image for a variant
  const handleRemoveExistingVariantImage = (key: string, idx: number) => {
    setVariantDetails(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        existingImages: prev[key]?.existingImages.filter((_, i) => i !== idx) || []
      }
    }));
  };

  // UI rendering
  return (
    <div className="space-y-6">
      {/* No options yet */}
      {optionDefs.length === 0 && (
        <button
          type="button"
          onClick={handleAddOption}
          className="flex items-center gap-2 text-blue-600 hover:underline font-medium px-2 py-2 rounded transition"
        >
          <span className="text-xl">+</span> Add options like size or color
        </button>
      )}

      {/* Option cards */}
      <div className="space-y-2">
        {optionDefs.map((option, idx) => (
          <div key={option.id} className="relative group bg-white border border-gray-200 p-4 flex flex-col gap-2">
            {/* Delete icon (show on hover) */}
            <button
              type="button"
              onClick={() => handleRemoveOption(option.id)}
              className="absolute right-3 top-3 text-gray-300 group-hover:text-red-500 transition-opacity opacity-0 group-hover:opacity-100"
              aria-label="Delete option"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Option name */}
            <div className="flex-1 min-w-[120px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">Option name</label>
              <select
                value={option.name || ''}
                onChange={e => handleOptionNameChange(option.id, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="" disabled>Select option...</option>
                {defaultOptionNames.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Option values (separate row) */}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Option values</label>
              <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 px-2 py-1 min-h-[44px]">
                {option.values.map((val) => (
                  <span key={val} className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 text-xs border border-blue-200">
                    {val}
                    <button
                      type="button"
                      className="ml-1 text-blue-600 hover:text-red-600"
                      onClick={() => handleRemoveValue(option.id, val)}
                      aria-label="Remove value"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={option.valueInput}
                  onChange={e => handleValueInputChange(option.id, e.target.value)}
                  placeholder="Type and press Enter"
                  className="flex-1 min-w-[80px] px-2 py-1 border-none bg-transparent focus:ring-0 text-sm"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddValue(option.id);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleAddValue(option.id)}
                  className="px-2 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add another option */}
      {optionDefs.length > 0 && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={handleAddOption}
            className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 border border-blue-100 font-medium px-4 py-2 transition"
          >
            <span className="text-xl">+</span> Add another option
          </button>
        </div>
      )}

      {/* Generated variants — each inherits the product's price/stock/SKU/photos unless customized */}
      {(() => {
        const combos = getCombinations(optionDefs);
        if (combos.length === 0) return null;
        const fmt = (n?: number) =>
          n === undefined || n === null || isNaN(n) ? "—" : `₹${n}`;
        return (
          <div className="mt-8 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <h4 className="font-semibold text-gray-800">Variants ({combos.length})</h4>
              <span className="text-xs text-gray-400">Each variant uses the product's price, stock &amp; photos unless you customize it.</span>
            </div>

            <div className="space-y-2">
              {combos.map((combo) => {
                const key = optionDefs.map(opt => combo[opt.name]).join("||");
                const details = variantDetails[key];
                const isCustom = customizedKeys.has(key);
                const label = optionDefs.map(opt => combo[opt.name]).join(" / ");

                // Effective (inherited-or-overridden) values shown in the summary line.
                const effMrp = details?.mrp ?? baseValues?.mrp;
                const effSelling = details?.sellingPrice ?? baseValues?.sellingPrice ?? effMrp;
                const effStock = details?.stock ?? baseValues?.stock;
                const effSku = details?.sku?.trim() || autoSkuForCombo(productTitle, combo);
                const usesOwnImages = ((details?.existingImages?.length || 0) + (details?.images?.length || 0)) > 0;

                if (!isCustom) {
                  return (
                    <div key={key} className="flex items-center justify-between gap-3 border border-gray-200 rounded px-4 py-3 bg-white">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800">{label}</div>
                        <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span>
                            {fmt(effSelling)}
                            {effMrp !== undefined && effSelling !== undefined && effMrp !== effSelling && (
                              <span className="line-through text-gray-400 ml-1">{fmt(effMrp)}</span>
                            )}
                          </span>
                          <span>· {effStock !== undefined ? `${effStock} in stock` : "stock —"}</span>
                          <span className="font-mono">· {effSku}{!details?.sku?.trim() && <span className="font-sans text-gray-400"> (auto)</span>}</span>
                          <span className="text-gray-400">· product photos</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleStartCustomize(key, combo)}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 px-3 py-1.5 rounded transition"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Customize
                      </button>
                    </div>
                  );
                }

                // Expanded per-variant editor.
                return (
                  <div key={key} className="border border-blue-200 rounded bg-blue-50/40 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-gray-800">{label}</div>
                      <button
                        type="button"
                        onClick={() => handleResetCustomize(key)}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-100 px-3 py-1.5 rounded transition"
                        title="Discard overrides and inherit from the product"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Use product defaults
                      </button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">MRP (₹)</label>
                        <input
                          type="number"
                          min={0}
                          value={details?.mrp !== undefined ? details.mrp : ""}
                          onChange={e => {
                            const value = e.target.value;
                            if (value === "") {
                              handleVariantDetailChange(key, 'mrp', undefined);
                            } else {
                              const numValue = parseFloat(value);
                              handleVariantDetailChange(key, 'mrp', isNaN(numValue) ? undefined : numValue);
                            }
                          }}
                          className="w-full px-2 py-1.5 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={baseValues?.mrp !== undefined ? String(baseValues.mrp) : "0"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Selling Price (₹)</label>
                        <input
                          type="number"
                          min={0}
                          value={details?.sellingPrice !== undefined ? details.sellingPrice : ""}
                          onChange={e => {
                            const value = e.target.value;
                            if (value === "") {
                              handleVariantDetailChange(key, 'sellingPrice', undefined);
                            } else {
                              const numValue = parseFloat(value);
                              handleVariantDetailChange(key, 'sellingPrice', isNaN(numValue) ? undefined : numValue);
                            }
                          }}
                          className="w-full px-2 py-1.5 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={baseValues?.sellingPrice !== undefined ? String(baseValues.sellingPrice) : "0"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Stock</label>
                        <input
                          type="number"
                          min={0}
                          value={details?.stock !== undefined ? details.stock : ""}
                          onChange={e => {
                            const value = e.target.value;
                            if (value === "") {
                              handleVariantDetailChange(key, 'stock', undefined);
                            } else {
                              const numValue = parseInt(value);
                              handleVariantDetailChange(key, 'stock', isNaN(numValue) ? undefined : numValue);
                            }
                          }}
                          className="w-full px-2 py-1.5 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={baseValues?.stock !== undefined ? String(baseValues.stock) : "0"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">SKU</label>
                        <input
                          type="text"
                          value={details?.sku !== undefined ? details.sku : ""}
                          onChange={e => handleVariantDetailChange(key, 'sku', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                          placeholder={`Auto: ${autoSkuForCombo(productTitle, combo)}-XXXX`}
                        />
                      </div>
                    </div>

                    {/* Variant images (optional — falls back to the product photos) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Variant Images <span className="font-normal text-gray-400">— optional, leave empty to use the product photos</span>
                      </label>
                      <div
                        className="border-2 border-dashed border-gray-300 p-4 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition bg-white mb-2 relative"
                        onClick={() => document.getElementById(`variant-image-input-${key}`)?.click()}
                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                        onDrop={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          const files = Array.from(e.dataTransfer.files);
                          const dt = { target: { files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                          handleVariantImageChange(key, dt.target.files as FileList);
                        }}
                        tabIndex={0}
                        aria-label="Upload variant images"
                      >
                        <Image className="w-8 h-8 text-gray-400 mb-1" />
                        <span className="text-gray-500 text-xs">Drag & drop images here, or <span className="text-blue-600 underline">click to upload</span></span>
                        <span className="text-[11px] text-gray-400 mt-1">Images are compressed on upload</span>
                        <input
                          id={`variant-image-input-${key}`}
                          type="file"
                          multiple
                          accept="image/*"
                          className="hidden"
                          onChange={e => handleVariantImageChange(key, e.target.files)}
                        />
                        {compressingVariants[key] && (
                          <div className="absolute inset-0 bg-white/70 flex items-center justify-center gap-2 rounded">
                            <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-[11px] text-gray-600 font-medium">Compressing...</span>
                          </div>
                        )}
                      </div>
                      {!usesOwnImages && (
                        <p className="text-[11px] text-gray-400 mb-1">Currently using the product photos.</p>
                      )}
                      {details?.images && details.images.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
                          {details.images.map((img, i) => (
                            <div key={i} className="relative group overflow-hidden border border-gray-200 bg-white">
                              <img
                                src={URL.createObjectURL(img)}
                                alt=""
                                className="object-cover w-full h-20 sm:h-24 transition-transform duration-300 group-hover:scale-105"
                              />
                              <button
                                type="button"
                                className="absolute top-1 right-1 text-white bg-red-500 hover:bg-red-600 p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                                onClick={e => { e.stopPropagation(); handleRemoveVariantImage(key, i); }}
                                aria-label="Remove image"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {details?.existingImages && details.existingImages.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Existing Images:</p>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {details.existingImages.map((imgUrl, i) => (
                              <div key={`existing-${i}`} className="relative group overflow-hidden border border-gray-200 bg-white">
                                <img
                                  src={imgUrl}
                                  alt=""
                                  className="object-cover w-full h-20 sm:h-24 transition-transform duration-300 group-hover:scale-105"
                                />
                                <button
                                  type="button"
                                  className="absolute top-1 right-1 text-white bg-red-500 hover:bg-red-600 p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleRemoveExistingVariantImage(key, i);
                                  }}
                                  aria-label="Remove existing image"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProductVariantManager; 
